const Integrations = () => {
  const platforms = [
    { name: 'Shopify', logo: 'shopify' },
    { name: 'WooCommerce', logo: 'woocommerce' },
    { name: 'Etsy', logo: 'etsy' },
    { name: 'BigCommerce', logo: 'bigcommerce' },
    { name: 'Squarespace', logo: 'squarespace' },
    { name: 'Wix', logo: 'wix' },
  ];

  // Double the array for seamless loop
  const doubledPlatforms = [...platforms, ...platforms];

  return (
    <section
      id="integrations"
      data-testid="integrations-section"
      className="py-24 md:py-32 bg-coastal-accent/30"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2
            data-testid="integrations-heading"
            className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold text-coastal-navy mb-6"
          >
            Ecommerce <span className="text-coastal-orange">integrations</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Connect your online store with Coastal Route, and each time you make
            a sale, we'll produce the order and deliver it straight to your
            customer.
          </p>
        </div>

        {/* Marquee */}
        <div
          data-testid="integrations-marquee"
          className="relative overflow-hidden py-8"
        >
          {/* Gradient Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-coastal-accent/30 to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-coastal-accent/30 to-transparent z-10" />

          {/* Scrolling Content */}
          <div className="flex animate-marquee gap-16 md:gap-24">
            {doubledPlatforms.map((platform, index) => (
              <div
                key={`${platform.name}-${index}`}
                className="flex items-center gap-3 flex-shrink-0"
              >
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <span className="text-2xl font-heading font-bold text-coastal-navy/30">
                    {platform.name.charAt(0)}
                  </span>
                </div>
                <span className="text-xl font-heading font-semibold text-coastal-navy/60 whitespace-nowrap">
                  {platform.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Integration Cards */}
        <div
          data-testid="integration-features"
          className="grid md:grid-cols-3 gap-6 mt-12"
        >
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="w-12 h-12 bg-coastal-orange/10 rounded-xl flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-coastal-orange"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="font-heading font-semibold text-coastal-navy mb-2">
              Automatic Sync
            </h3>
            <p className="text-gray-600 text-sm">
              Orders sync automatically from your store. No manual work needed.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="w-12 h-12 bg-coastal-orange/10 rounded-xl flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-coastal-orange"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <h3 className="font-heading font-semibold text-coastal-navy mb-2">
              Real-time Tracking
            </h3>
            <p className="text-gray-600 text-sm">
              Tracking info syncs back to your store for customer visibility.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="w-12 h-12 bg-coastal-orange/10 rounded-xl flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-coastal-orange"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="font-heading font-semibold text-coastal-navy mb-2">
              Transparent Pricing
            </h3>
            <p className="text-gray-600 text-sm">
              See production costs upfront. Set your margins with confidence.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="#contact"
            data-testid="integrations-cta"
            className="inline-flex items-center gap-2 text-coastal-navy font-semibold hover:text-coastal-orange transition-colors"
          >
            Connect Your Store
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Integrations;
